/**
 * Billing API client — plus an in-memory mock that satisfies the same
 * contract while the backend is being built. Toggle via `VITE_BILLING_MOCK`.
 *
 * The mock supports mutations (top-up, upgrade, auto-recharge config) so
 * the UI can be smoke-tested end-to-end without Stripe. State lives in a
 * module-level singleton that survives across component re-renders but
 * resets on full page reload — that's the right shape for dev review.
 */

import { env } from "@/env";
import apiClient from "@lib/axios";
import type {
  AutoRechargeConfig,
  Invoice,
  PaymentMethod,
  Plan,
  SeatUsage,
  TopUpPackId,
  UsageBreakdown,
  UsageRange,
  Wallet,
} from "@models/billing";
import { TOP_UP_PACKS } from "@models/billing";

// ── Real-API surface ────────────────────────────────────────────────────────

/**
 * Each method takes the optional billing scope (orgId or undefined for
 * personal) and an optional `init` for cancellation; non-members get
 * a zeroed payload.
 *
 * URLs assume the backend will mount these at `/billing/*`; the existing
 * apiClient handles `/v1` versioning, auth cookies, and 401 redirects.
 */
function orgQuery(organisationId?: string | null): string {
  return organisationId
    ? `?organisation_id=${encodeURIComponent(organisationId)}`
    : "";
}

const realApi = {
  getPlan: async (orgId?: string | null): Promise<Plan> => {
    const { data } = await apiClient.get<Plan>(
      `/billing/plan${orgQuery(orgId)}`,
    );
    return data;
  },
  getWallet: async (orgId?: string | null): Promise<Wallet> => {
    const { data } = await apiClient.get<Wallet>(
      `/billing/wallet${orgQuery(orgId)}`,
    );
    return data;
  },
  getUsageBreakdown: async (
    range: UsageRange,
    orgId?: string | null,
  ): Promise<UsageBreakdown> => {
    const params = new URLSearchParams({ range });
    if (orgId) params.set("organisation_id", orgId);
    const { data } = await apiClient.get<UsageBreakdown>(
      `/billing/usage?${params.toString()}`,
    );
    return data;
  },
  getInvoices: async (orgId?: string | null): Promise<Invoice[]> => {
    const { data } = await apiClient.get<Invoice[]>(
      `/billing/invoices${orgQuery(orgId)}`,
    );
    return data;
  },
  getPaymentMethods: async (
    orgId?: string | null,
  ): Promise<PaymentMethod[]> => {
    const { data } = await apiClient.get<PaymentMethod[]>(
      `/billing/payment-methods${orgQuery(orgId)}`,
    );
    return data;
  },
  getSeatUsage: async (orgId: string): Promise<SeatUsage[]> => {
    const { data } = await apiClient.get<SeatUsage[]>(
      `/billing/seats?organisation_id=${encodeURIComponent(orgId)}`,
    );
    return data;
  },
  /**
   * Returns a Stripe Checkout URL for the requested plan change. Caller
   * redirects via `window.location.href` so the browser navigates to the
   * Stripe-hosted page; on completion Stripe returns to `?upgrade=success`.
   */
  createCheckoutSession: async (args: {
    planId: "pro" | "team";
    seats?: number;
    organisationId?: string | null;
  }): Promise<{ url: string }> => {
    const { data } = await apiClient.post<{ url: string }>(
      "/billing/checkout/subscription",
      args,
    );
    return data;
  },
  createTopupSession: async (args: {
    packId: TopUpPackId;
    organisationId?: string | null;
  }): Promise<{ url: string }> => {
    const { data } = await apiClient.post<{ url: string }>(
      "/billing/checkout/topup",
      args,
    );
    return data;
  },
  configureAutoRecharge: async (
    cfg: AutoRechargeConfig,
    orgId?: string | null,
  ): Promise<AutoRechargeConfig> => {
    const { data } = await apiClient.put<AutoRechargeConfig>(
      `/billing/auto-recharge${orgQuery(orgId)}`,
      cfg,
    );
    return data;
  },
  /** Stripe customer-portal deep-link to "Manage payment methods". */
  createPortalLink: async (orgId?: string | null): Promise<{ url: string }> => {
    const { data } = await apiClient.post<{ url: string }>(
      `/billing/portal${orgQuery(orgId)}`,
    );
    return data;
  },
};

// ── Mock implementation ─────────────────────────────────────────────────────

interface ScopeState {
  plan: Plan;
  wallet: Wallet;
  invoices: Invoice[];
  paymentMethods: PaymentMethod[];
  seatUsage: SeatUsage[];
}

function nowIso(): string {
  return new Date().toISOString();
}

function daysFromNow(days: number): string {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

function freshTrialState(scope: string): ScopeState {
  const trialEnd = daysFromNow(11);
  return {
    plan: {
      id: "trial",
      display_name: "Trial",
      price_cents: 0,
      interval: "month",
      seats: 1,
      current_period_end: trialEnd,
      read_only: false,
      past_due: false,
      trial_ends_at: trialEnd,
    },
    wallet: {
      scope: scope === "personal" ? "personal" : { org_id: scope },
      balance: 312,
      trial_credits: 312,
      included_credits: 0,
      rollover_credits: 0,
      topup_credits: 0,
      next_reset_at: trialEnd,
      auto_recharge: { enabled: false, threshold_credits: 200, pack_id: "p1k" },
    },
    invoices: [],
    paymentMethods: [],
    seatUsage: [],
  };
}

function freshProState(scope: string): ScopeState {
  const cycleEnd = daysFromNow(18);
  const cycleStart = daysFromNow(-12);
  return {
    plan: {
      id: "pro",
      display_name: "Pro",
      price_cents: 2500,
      interval: "month",
      seats: 1,
      current_period_end: cycleEnd,
      read_only: false,
      past_due: false,
      trial_ends_at: null,
    },
    wallet: {
      scope: scope === "personal" ? "personal" : { org_id: scope },
      balance: 1847,
      trial_credits: 0,
      included_credits: 1500,
      rollover_credits: 200,
      topup_credits: 147,
      next_reset_at: cycleEnd,
      auto_recharge: { enabled: false, threshold_credits: 200, pack_id: "p1k" },
    },
    invoices: [
      {
        id: "in_mock_3",
        issued_at: cycleStart,
        description: "Pro · April 2026",
        amount_cents: 2500,
        status: "paid",
        pdf_url: "#",
      },
      {
        id: "in_mock_2",
        issued_at: daysFromNow(-22),
        description: "Top-up · 5,500 credits",
        amount_cents: 5000,
        status: "paid",
        pdf_url: "#",
      },
      {
        id: "in_mock_1",
        issued_at: daysFromNow(-42),
        description: "Pro · March 2026",
        amount_cents: 2500,
        status: "paid",
        pdf_url: "#",
      },
    ],
    paymentMethods: [
      {
        id: "pm_mock_1",
        brand: "visa",
        last4: "4242",
        exp: "08/29",
        is_default: true,
      },
    ],
    seatUsage: [],
  };
}

const mockStateByScope = new Map<string, ScopeState>();

/**
 * Force a particular plan when initialising a mock scope. Toggle this in
 * dev tools if you want to preview "Pro" or "Team" surfaces from a fresh
 * reload. Reads `localStorage.cf_billing_mock_plan` lazily.
 */
function initialState(scope: string): ScopeState {
  const override =
    typeof window !== "undefined"
      ? window.localStorage.getItem("cf_billing_mock_plan")
      : null;
  if (override === "pro") return freshProState(scope);
  if (override === "trial") return freshTrialState(scope);
  return freshTrialState(scope);
}

function getState(scope: string): ScopeState {
  let s = mockStateByScope.get(scope);
  if (!s) {
    s = initialState(scope);
    mockStateByScope.set(scope, s);
  }
  return s;
}

function scopeOf(orgId?: string | null): string {
  return orgId || "personal";
}

function delay<T>(value: T, ms = 250): Promise<T> {
  return new Promise((res) => setTimeout(() => res(value), ms));
}

function syntheticUsage(scope: string, range: UsageRange): UsageBreakdown {
  const days = range === "7d" ? 7 : range === "30d" ? 30 : 30;
  const seed = scope.length;
  const points: UsageBreakdown["points"] = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    // Deterministic-ish jitter so the chart doesn't reshuffle on every
    // render but still looks alive.
    const x = Math.sin((i + seed) * 1.7) * 0.5 + 0.5;
    const y = Math.sin((i + seed) * 0.7 + 1.3) * 0.5 + 0.5;
    const sonnet = Math.round(20 + 100 * x);
    const opus = Math.round(5 + 60 * y);
    const subagents = Math.round(3 + 25 * (1 - x));
    const spice = i % 6 === 0 ? 50 : 0;
    points.push({
      date,
      totals: {
        sonnet_chat: sonnet,
        opus_chat: opus,
        subagents,
        spice,
      },
    });
  }
  return {
    scope: scope === "personal" ? "personal" : { org_id: scope },
    range,
    points,
  };
}

const mockApi: typeof realApi = {
  getPlan: async (orgId) => {
    const s = getState(scopeOf(orgId));
    return delay({ ...s.plan });
  },
  getWallet: async (orgId) => {
    const s = getState(scopeOf(orgId));
    return delay({
      ...s.wallet,
      balance:
        s.wallet.trial_credits +
        s.wallet.included_credits +
        s.wallet.rollover_credits +
        s.wallet.topup_credits,
    });
  },
  getUsageBreakdown: async (range, orgId) => {
    return delay(syntheticUsage(scopeOf(orgId), range));
  },
  getInvoices: async (orgId) => {
    const s = getState(scopeOf(orgId));
    return delay([...s.invoices]);
  },
  getPaymentMethods: async (orgId) => {
    const s = getState(scopeOf(orgId));
    return delay([...s.paymentMethods]);
  },
  getSeatUsage: async (orgId) => {
    const s = getState(orgId);
    return delay([...s.seatUsage]);
  },

  createCheckoutSession: async ({ planId, seats = 1, organisationId }) => {
    const scope = scopeOf(organisationId);
    const s = getState(scope);
    if (planId === "pro") {
      Object.assign(s, freshProState(scope));
    } else {
      // Team — pretend; we don't model multi-seat in the mock yet.
      Object.assign(s, freshProState(scope));
      s.plan.id = "team";
      s.plan.display_name = "Team Owner";
      s.plan.price_cents = 3000 * seats;
      s.plan.seats = seats;
      s.wallet.included_credits = 5_000 * seats;
    }
    s.invoices = [
      {
        id: `in_mock_${Date.now()}`,
        issued_at: nowIso(),
        description: `${s.plan.display_name} · Initial cycle`,
        amount_cents: s.plan.price_cents,
        status: "paid",
        pdf_url: "#",
      },
      ...s.invoices,
    ];
    return delay({
      url: `${window.location.origin}/settings/billing?upgrade=success`,
    });
  },

  createTopupSession: async ({ packId, organisationId }) => {
    const scope = scopeOf(organisationId);
    const s = getState(scope);
    const pack = TOP_UP_PACKS.find((p) => p.id === packId);
    if (!pack) throw new Error(`Unknown pack ${packId}`);
    s.wallet.topup_credits += pack.credits;
    s.invoices = [
      {
        id: `in_mock_${Date.now()}`,
        issued_at: nowIso(),
        description: `Top-up · ${pack.credits.toLocaleString()} credits`,
        amount_cents: pack.price_cents,
        status: "paid",
        pdf_url: "#",
      },
      ...s.invoices,
    ];
    return delay({
      url: `${window.location.origin}/settings/billing?topup=success`,
    });
  },

  configureAutoRecharge: async (cfg, orgId) => {
    const s = getState(scopeOf(orgId));
    s.wallet.auto_recharge = { ...cfg };
    return delay({ ...cfg });
  },

  createPortalLink: async (orgId) => {
    // No real Stripe portal in the mock — bounce back to the billing page.
    void orgId;
    return delay({ url: `${window.location.origin}/settings/billing` });
  },
};

// ── Public surface ──────────────────────────────────────────────────────────

export const billingApi: typeof realApi =
  env.VITE_BILLING_MOCK === "1" ? mockApi : realApi;

/**
 * Dev-only: drop the mock state so the next render starts from a clean
 * `freshTrialState`. Wire to a hidden keyboard shortcut later if useful.
 */
export function __resetBillingMock(): void {
  mockStateByScope.clear();
}
